import { useState } from 'react';
import { Control, Controller, FieldValues } from 'react-hook-form';
import {
    FormInputText,
    Grid,
    Typography,
    FormUploadFile,
    FormTextEditor,
    Box,
    Popover,
    IconButton,
    Icon,
} from '../../../base';
import { UploadFileTypes } from '../../../common';
import { useTranslation } from 'react-i18next';
import { SketchPicker } from 'react-color';
import {
    StyledColorSwatch,
    StyledColorPickerRow,
    StyledColorHexText,
} from './CompanyInformationTab.styles';

interface Props {
    control: Control<FieldValues>;
}

interface ColorPickerFieldProps {
    label: string;
    name: string;
    control: Control<FieldValues>;
}

const ColorPickerField = ({ label, name, control }: ColorPickerFieldProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    return (
        <Controller
            name={name}
            control={control}
            render={({ field }) => (
                <>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {label}
                    </Typography>
                    <StyledColorPickerRow>
                        <StyledColorSwatch
                            sx={{ backgroundColor: field.value || '#ffffff' }}
                            onClick={(e) =>
                                setAnchorEl(e.currentTarget as HTMLElement)
                            }
                        />
                        <StyledColorHexText>
                            {field.value || '—'}
                        </StyledColorHexText>
                    </StyledColorPickerRow>
                    <Popover
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        onClose={() => setAnchorEl(null)}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                    >
                        <SketchPicker
                            color={field.value || '#ffffff'}
                            onChangeComplete={(color) =>
                                field.onChange(color.hex)
                            }
                            styles={{
                                default: {
                                    picker: {
                                        border: 'none',
                                        boxShadow: 'none',
                                    },
                                },
                            }}
                        />
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                p: 1,
                                paddingInlineEnd: 2,
                            }}
                        >
                            <IconButton
                                onClick={() => setAnchorEl(null)}
                                size="small"
                                sx={{ height: 18, width: 18 }}
                            >
                                <Icon name="CheckRoundedIcon" size={16} />
                            </IconButton>
                        </Box>
                    </Popover>
                </>
            )}
        />
    );
};

const CompanyInformationTab = ({ control }: Props) => {
    const { t } = useTranslation();
    return (
        <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={4}>
                <FormInputText
                    name="companyName"
                    control={control}
                    label={t('settings.companyInformation.companyName')}
                    placeholder={t(
                        'settings.companyInformation.companyNamePlaceholder'
                    )}
                    disabled
                />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
                <FormInputText
                    name="companyId"
                    control={control}
                    label={t('settings.companyInformation.companyId')}
                    placeholder={t(
                        'settings.companyInformation.companyIdPlaceholder'
                    )}
                    disabled
                />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
                <FormInputText
                    name="phoneNumber"
                    control={control}
                    label={t('settings.companyInformation.phoneNumber')}
                    placeholder={t(
                        'settings.companyInformation.phoneNumberPlaceholder'
                    )}
                />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
                <FormInputText
                    name="hqAddress"
                    control={control}
                    label={t('settings.companyInformation.hqAddress')}
                    placeholder={t(
                        'settings.companyInformation.hqAddressPlaceholder'
                    )}
                />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
                <FormInputText
                    name="companyWebsite"
                    control={control}
                    label={t('settings.companyInformation.companyWebsite')}
                    placeholder={t(
                        'settings.companyInformation.companyWebsitePlaceholder'
                    )}
                />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
                <FormInputText
                    name="recruitingEmailAddress"
                    control={control}
                    label={t('settings.companyInformation.recruitingEmail')}
                    placeholder={t(
                        'settings.companyInformation.recruitingEmailPlaceholder'
                    )}
                />
            </Grid>
            <Grid item xs={12} lg={8}>
                <Typography variant="subtitle2">
                    {t('settings.companyInformation.companyDescription')}
                </Typography>
                <FormTextEditor name="companyDescription" control={control} />
            </Grid>
            <Grid item xs={12} lg={4}>
                <Typography variant="subtitle2">
                    {t('settings.companyInformation.companyLogo')}
                </Typography>
                <FormUploadFile
                    name="companyLogo"
                    type={UploadFileTypes.image}
                    enableEditor={true}
                    maxWidth={1200}
                    maxHeight={1200}
                    quality={0.92}
                />
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                        <ColorPickerField
                            name="primaryColor"
                            label={t('settings.companyInformation.primaryColor')}
                            control={control}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <ColorPickerField
                            name="secondaryColor"
                            label={t('settings.companyInformation.secondaryColor')}
                            control={control}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default CompanyInformationTab;
